import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Heart, ShieldCheck, Sparkles, Search, FileText,
  ChevronRight, X, TrendingUp, BadgeCheck,
} from "lucide-react";

/**
 * NonprofitOnboardingWizard
 * -------------------------
 * A 3-step first-run wizard shown to freshly-signed-up nonprofits on
 * /verify-nonprofit. Prevents the "here's a form with an EIN field, good
 * luck" cliff that made verification feel like a chore.
 *
 * Steps:
 *   1. Welcome — what verification unlocks (50% off Pro, verified badge,
 *      priority discovery). Sets expectations ("2 minutes, no upload if
 *      you're already listed with the IRS").
 *   2. Find your EIN — where to look (IRS determination letter,
 *      IRS.gov EIN lookup, tax filings). Reduces the fear of not
 *      remembering the number.
 *   3. Ready — big "Start verifying" CTA that scrolls to the EIN form.
 *
 * Dismissal:
 *   - Stored per-user in localStorage under `avt:np_wizard_seen:<user_id>`
 *     so a completed wizard doesn't nag on later visits.
 *   - "Skip intro" button on every step for users who know the drill.
 *   - Rendered nothing when already dismissed OR when the user is
 *     already verified/pending/rejected (they don't need onboarding).
 */
export default function NonprofitOnboardingWizard({ user, status, onStart }) {
  const storageKey = user?.id ? `avt:np_wizard_seen:${user.id}` : null;
  const [step, setStep] = useState(1);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show only for freshly-signed-up nonprofits who haven't seen it yet.
    if (!storageKey || !status) return;
    const alreadyDismissed = localStorage.getItem(storageKey) === "1";
    const canSee = status.nonprofit_status === "unverified"
      && (user?.signup_intent === "nonprofit" || user?.is_nonprofit);
    setVisible(canSee && !alreadyDismissed);
  }, [storageKey, status, user?.signup_intent, user?.is_nonprofit]);

  const dismiss = () => {
    if (storageKey) {
      try { localStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
    }
    setVisible(false);
    onStart?.();
  };

  if (!visible) return null;

  return (
    <div
      data-testid="np-wizard"
      className="mt-8 rounded-2xl overflow-hidden"
      style={{
        background: "linear-gradient(135deg, rgba(107,47,186,0.06), rgba(249,115,22,0.06))",
        border: "2px solid rgba(107,47,186,0.25)",
        boxShadow: "0 20px 50px -30px rgba(107,47,186,0.35)",
      }}
    >
      {/* Header — progress + skip */}
      <div className="flex items-center justify-between px-5 pt-5">
        <div className="flex items-center gap-2" data-testid="np-wizard-progress">
          {[1, 2, 3].map((n) => (
            <span
              key={n}
              data-testid={`np-wizard-dot-${n}`}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: n === step ? 28 : 12,
                backgroundColor: n <= step ? "var(--avt-purple)" : "rgba(107,47,186,0.18)",
              }}
            />
          ))}
          <span className="ml-2 text-xs" style={{ color: "var(--ink-soft)" }}>
            Step {step} of 3
          </span>
        </div>
        <button
          type="button"
          data-testid="np-wizard-skip"
          onClick={dismiss}
          className="text-xs inline-flex items-center gap-1 hover:underline"
          style={{ color: "var(--ink-soft)" }}
        >
          <X className="h-3 w-3" /> Skip intro
        </button>
      </div>

      <div className="px-5 sm:px-8 pb-6 pt-4">
        {step === 1 && <StepWelcome onNext={() => setStep(2)} />}
        {step === 2 && <StepFindEIN onBack={() => setStep(1)} onNext={() => setStep(3)} />}
        {step === 3 && <StepReady onBack={() => setStep(2)} onStart={dismiss} />}
      </div>
    </div>
  );
}

/* ---------- Step 1: Welcome ---------- */
function StepWelcome({ onNext }) {
  const perks = [
    { icon: Sparkles, label: "50% off Maker Pro or Organizer Pro forever" },
    { icon: BadgeCheck, label: "Verified nonprofit badge on every profile + card" },
    { icon: TrendingUp, label: "Priority placement in event and vendor discovery" },
  ];
  return (
    <div data-testid="np-wizard-step-1">
      <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full"
           style={{ backgroundColor: "rgba(132,204,22,0.14)", color: "#4B7A0A", border: "1.5px solid #84CC16" }}>
        <Heart className="h-3 w-3" fill="#84CC16" strokeWidth={0} /> A quick intro
      </div>
      <h2 className="font-serif text-3xl mt-3 leading-tight" data-testid="np-wizard-step-1-title">
        <span style={{ color: "var(--avt-purple)" }}>Two minutes</span> to unlock everything.
      </h2>
      <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
        Verified nonprofits get real perks on AddVendTour. If your org is already listed
        with the IRS, we can confirm you instantly — no paperwork.
      </p>

      <div className="mt-5 space-y-2.5" data-testid="np-wizard-perks">
        {perks.map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-start gap-3 text-sm">
            <span className="mt-0.5 rounded-full p-1.5"
                  style={{ backgroundColor: "rgba(107,47,186,0.10)", color: "var(--avt-purple)" }}>
              <Icon className="h-4 w-4" />
            </span>
            <span>{label}</span>
          </div>
        ))}
      </div>

      <Button
        onClick={onNext}
        data-testid="np-wizard-next-1"
        className="btn-primary mt-6 px-5 py-2.5 rounded-full inline-flex items-center gap-2"
      >
        Show me what I need <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

/* ---------- Step 2: Find your EIN ---------- */
function StepFindEIN({ onBack, onNext }) {
  const places = [
    {
      icon: FileText,
      title: "IRS determination letter",
      body: "The 501(c)(3) approval letter from the IRS shows your EIN in the top-right corner. Formatted as XX-XXXXXXX.",
    },
    {
      icon: Search,
      title: "IRS Tax Exempt Organization Search",
      body: (
        <>
          Free lookup by org name at{" "}
          <a
            href="https://apps.irs.gov/app/eos/"
            target="_blank"
            rel="noreferrer"
            className="underline"
            data-testid="np-wizard-irs-link"
            style={{ color: "var(--avt-teal)" }}
          >
            apps.irs.gov/app/eos
          </a>
          .
        </>
      ),
    },
    {
      icon: ShieldCheck,
      title: "Form 990 tax filing",
      body: "Any past Form 990 also shows the EIN on page 1. ProPublica's Nonprofit Explorer also lists it publicly.",
    },
  ];
  return (
    <div data-testid="np-wizard-step-2">
      <h2 className="font-serif text-3xl mt-1 leading-tight" data-testid="np-wizard-step-2-title">
        Grab your <span style={{ color: "var(--avt-teal)" }}>9-digit EIN</span>.
      </h2>
      <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
        It looks like <code className="px-1.5 py-0.5 rounded"
          style={{ backgroundColor: "rgba(44,196,217,0.14)", color: "var(--avt-teal)" }}>12-3456789</code>.
        Here are three places to find it:
      </p>

      <div className="mt-5 space-y-3" data-testid="np-wizard-places">
        {places.map((p, i) => (
          <div key={i} className="flex items-start gap-3 rounded-lg p-3"
               style={{ backgroundColor: "rgba(255,255,255,0.55)" }}>
            <span className="mt-0.5 rounded-full p-2"
                  style={{ backgroundColor: "rgba(44,196,217,0.14)", color: "var(--avt-teal)" }}>
              <p.icon className="h-4 w-4" />
            </span>
            <div>
              <div className="text-sm font-semibold">{p.title}</div>
              <div className="text-xs mt-0.5" style={{ color: "var(--ink-soft)" }}>{p.body}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <Button
          onClick={onBack}
          data-testid="np-wizard-back-2"
          variant="outline"
          className="rounded-full px-5 py-2.5"
        >
          Back
        </Button>
        <Button
          onClick={onNext}
          data-testid="np-wizard-next-2"
          className="btn-primary px-5 py-2.5 rounded-full inline-flex items-center gap-2"
        >
          I have my EIN <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/* ---------- Step 3: Ready ---------- */
function StepReady({ onBack, onStart }) {
  return (
    <div data-testid="np-wizard-step-3">
      <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full"
           style={{ backgroundColor: "rgba(249,115,22,0.14)", color: "var(--avt-orange)", border: "1.5px solid var(--avt-orange)" }}>
        <Sparkles className="h-3 w-3" /> Last step
      </div>
      <h2 className="font-serif text-3xl mt-3 leading-tight" data-testid="np-wizard-step-3-title">
        Ready when you are.
      </h2>
      <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
        We&rsquo;ll check the free public IRS list via ProPublica. If your org is there,
        you&rsquo;ll be verified in about 5 seconds and your 50%-off pricing kicks in
        instantly. If not, you&rsquo;ll be able to upload your IRS letter and an admin
        will review it within one business day.
      </p>

      <div className="mt-5 flex flex-wrap gap-3">
        <Button
          onClick={onBack}
          data-testid="np-wizard-back-3"
          variant="outline"
          className="rounded-full px-5 py-2.5"
        >
          Back
        </Button>
        <Button
          onClick={onStart}
          data-testid="np-wizard-start-btn"
          className="btn-primary px-6 py-2.5 rounded-full inline-flex items-center gap-2"
        >
          <ShieldCheck className="h-4 w-4" /> Start verifying
        </Button>
      </div>
    </div>
  );
}
